<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, inject } from "vue"
import { useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { useDebounceFn } from "@vueuse/core"
import { useFetchWrapper } from "@core/composables"
import { useAuthStore } from "@core/stores/auth.store"
import type { Services } from "@services"
import type { Address } from "@types"
import { LEGAL_FORM_OPTIONS } from "@core/composables/useCompanyForm"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Finalisez votre compte",
            password: "Mot de passe",
            confirmPassword: "Confirmer le mot de passe",
            submit: "Valider",
            successMessage: "Compte finalisé. Redirection…",
            companyAddress: "Adresse de l'entreprise",
            companyId: "N° SIREN (9 chiffres)",
            companyLegalForm: "Forme juridique",
            errorIdCompany:
                "Le numéro SIREN doit contenir exactement 9 chiffres",
            promptLabel: "Choisir un mot de passe",
            weak: "Faible",
            medium: "Moyen",
            strong: "Fort",
            ruleMinLength: "Minimum 8 caractères",
            ruleLowercase: "Au moins une minuscule",
            ruleUppercase: "Au moins une majuscule",
            ruleNumeric: "Au moins un chiffre",
            message:
                "Veuillez renseigner les informations suivantes pour finaliser votre compte.",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    /** Utilisateur (issu des étapes précédentes). */
    user: { _id?: string; username?: string } | null
    /** Code de validation saisi à l'étape confirmation. */
    validation: string | null
    /** ID entreprise (SIREN) si déjà renseigné. */
    idcompany?: string | null
    /** L'utilisateur a une entreprise à renseigner. */
    hascompany?: boolean
}>()

// ================================
// COMPOSABLES
// ================================
const router = useRouter()
const authStore = useAuthStore()
const services = inject<Services>("services")!

// ================================
// STATE
// ================================
/** Mot de passe et confirmation */
const userForm = ref({ password: "", confirmPassword: "" })
/** Données entreprise (si hascompany && !idcompany) */
const companyForm = ref<{
    address: Address | null
    idcompany: string
    legalForm: string
}>({
    address: null,
    idcompany: "",
    legalForm: "",
})

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Vérifie que le mot de passe respecte les règles (longueur, majuscule, minuscule, chiffre).
 */
function validatePassword(p: string): boolean {
    return Boolean(
        p && p.length >= 8 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /\d/.test(p)
    )
}

/** Options de forme juridique pour le select */
const getLegalFormOptions = computed(() =>
    LEGAL_FORM_OPTIONS.map((lf) => ({ label: lf, value: lf }))
)

/** Erreur sur l'identifiant entreprise (SIREN 9 chiffres) */
const getCompanyIdError = computed(() => {
    const id = companyForm.value.idcompany
    if (!id) return null
    return /^\d{9}$/.test(id) ? null : t("errorIdCompany")
})

/** Formulaire valide */
const getFormIsValid = computed(() => {
    const pwdOk =
        validatePassword(userForm.value.password) &&
        userForm.value.password === userForm.value.confirmPassword
    if (!props.hascompany || props.idcompany) return pwdOk
    return (
        pwdOk &&
        !!companyForm.value.address &&
        !!companyForm.value.legalForm &&
        !!companyForm.value.idcompany &&
        !getCompanyIdError.value
    )
})

/** Wrapper soumission : mise à jour mot de passe + optionnellement entreprise + connexion */
const {
    fetch: submitFetch,
    isFetching: isSubmitFetching,
    hasError: hasSubmitError,
    error: submitErrorMessage,
    isSuccess: isSubmitSuccess,
} = useFetchWrapper(async () => {
    const userId = props.user?._id ?? ""
    const validationCode = props.validation ?? ""

    await services.$updateUserPassword({
        userId,
        password: userForm.value.password,
        validationCode,
    })

    if (props.hascompany && !props.idcompany) {
        await services.$updateUserCompany({
            userId,
            validationCode,
            dataCompany: {
                address: companyForm.value.address,
                idcompany: companyForm.value.idcompany,
                legalForm: companyForm.value.legalForm,
            },
        })
    }

    const loginResponse = await services.$login(
        props.user!.username!,
        userForm.value.password
    )
    if (loginResponse.token) {
        authStore.setToken(loginResponse.token)
        await authStore.fetchUser()
    }
    if ((loginResponse as any).multipleCompany) {
        await router.push({
            path: "/login",
            query: {
                view: "multi-company",
                username: props.user?.username,
                user: props.user?._id,
            },
        })
        return
    }
    setTimeout(() => router.replace("/"), 2000)
})

/** Soumission debounced (500 ms) pour éviter les clics répétés. */
const debouncedSubmit = useDebounceFn(() => {
    submitFetch()
}, 500)

/** Soumet le formulaire. */
function onSubmit(): void {
    if (!getFormIsValid.value) return
    debouncedSubmit()
}
</script>

<template>
    <form class="flex flex-col gap-4 w-full" @submit.prevent="onSubmit">
        <p class="text-sm text-surface-600">
            {{ t("message") }}
        </p>

        <template v-if="hascompany && !idcompany">
            <BaseInputWrapper
                input-id="first-login-company-address"
                :label="t('companyAddress')"
                :disabled="isSubmitSuccess"
            >
                <InputAddress
                    id="first-login-company-address"
                    v-model="companyForm.address"
                    class="w-full"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                input-id="first-login-company-id"
                :label="t('companyId')"
                :error="getCompanyIdError ?? undefined"
                :disabled="isSubmitSuccess"
            >
                <BaseInputText
                    id="first-login-company-id"
                    v-model="companyForm.idcompany"
                    type="text"
                    maxlength="9"
                    class="w-full"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                input-id="first-login-company-legal-form"
                :label="t('companyLegalForm')"
                :disabled="isSubmitSuccess"
            >
                <BaseSelect
                    id="first-login-company-legal-form"
                    v-model="companyForm.legalForm"
                    :options="getLegalFormOptions"
                    option-label="label"
                    option-value="value"
                    class="w-full"
                />
            </BaseInputWrapper>
        </template>

        <BaseInputWrapper
            input-id="first-login-password"
            :label="t('password')"
            :disabled="isSubmitSuccess"
        >
            <BasePassword
                id="first-login-password"
                v-model="userForm.password"
                toggle-mask
                fluid
                class="w-full"
                :prompt-label="t('promptLabel')"
                :weak-label="t('weak')"
                :medium-label="t('medium')"
                :strong-label="t('strong')"
            >
                <template #footer>
                    <BaseDivider />
                    <ul
                        class="pl-2 my-0 leading-normal text-sm text-surface-600"
                    >
                        <li>{{ t("ruleMinLength") }}</li>
                        <li>{{ t("ruleLowercase") }}</li>
                        <li>{{ t("ruleUppercase") }}</li>
                        <li>{{ t("ruleNumeric") }}</li>
                    </ul>
                </template>
            </BasePassword>
        </BaseInputWrapper>
        <BaseInputWrapper
            input-id="first-login-password-confirm"
            :label="t('confirmPassword')"
            :disabled="isSubmitSuccess"
        >
            <BasePassword
                id="first-login-password"
                v-model="userForm.confirmPassword"
                fluid
                class="w-full"
                toggle-mask
                :feedback="false"
            >
            </BasePassword>
        </BaseInputWrapper>

        <BaseButton
            type="submit"
            :label="t('submit')"
            :loading="isSubmitFetching"
            :disabled="!getFormIsValid || isSubmitFetching || isSubmitSuccess"
            class="w-full"
        />

        <BaseMessage v-if="hasSubmitError" severity="error">
            {{ submitErrorMessage?.message ?? t("submitError") }}
        </BaseMessage>
        <BaseMessage v-if="isSubmitSuccess" severity="success">
            {{ t("successMessage") }}
        </BaseMessage>
    </form>
</template>
